import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { from, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SessionManagerService } from '../services/session-manager.service';
import { environment } from '../../../environments/environment';

export const HttpsRequestInterceptor: HttpInterceptorFn = (req, next) => {
  // As chamadas de discovery e de token da angular-oauth2-oidc usam este mesmo HttpClient.
  // Elas não levam Bearer e não podem disparar renovação em 401 — isso criaria um laço.
  if (req.url.startsWith(environment.keycloak.issuer)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const sessionManager = inject(SessionManagerService);

  const comTokenAtual = (original: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = authService.getAccessToken();
    return token ? original.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : original;
  };

  return next(comTokenAtual(req)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        // 403 com "sem-acesso" é tratado por quem fez a chamada (ex.: loadUserData).
        return throwError(() => err);
      }

      // Rede de segurança: a verificação periódica pode não ter rodado a tempo
      // (relógio dessincronizado, aba suspensa). Uma única tentativa de renovação.
      return from(sessionManager.renovarAgora()).pipe(
        switchMap((renovado) => {
          if (!renovado) {
            authService.sessionTimeout();
            return throwError(() => err);
          }

          // Reenvio fora do `catchError` de cima, para não reentrar em renovação (laço).
          // Um 401 aqui é com o token recém-renovado: a API não aceita mais esta sessão,
          // então encerra — senão cada chamada seguinte dispararia outro grant_type=refresh_token.
          return next(comTokenAtual(req)).pipe(
            catchError((erroDoReenvio: unknown) => {
              if (erroDoReenvio instanceof HttpErrorResponse && erroDoReenvio.status === 401) {
                authService.sessionTimeout();
              }
              return throwError(() => erroDoReenvio);
            })
          );
        })
      );
    })
  );
};
